import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import logger from '@eneco/common/log';
import Cookies from 'js-cookie';

import type { PersonalizationCookieControllerContent } from './personalization-cookie-controller-content.types';

export const PERSONALIZATION_COOKIE_KEY = 'xp_pers';

// These keys must be exposed from Sitecore, however they are not.
export const ACTION_KEYS = {
  addOrUpdate: 'addKeyOrUpdateValue',
  remove: 'removeKeyValue',
} as const;

const normalize = (cookie: string): string => {
  let start = 0;
  let end = cookie.length;

  while (start < end && cookie[start] === '|') start += 1;
  while (end > start && cookie[end - 1] === '|') end -= 1;

  const trimmed = cookie.slice(start, end);
  return trimmed ? `|${trimmed}|` : '';
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const removeKeyFromCookie = (cookieValue: string, key: string): string =>
  cookieValue.replace(new RegExp(String.raw`\|${escapeRegExp(key)}:[^|]*\|`, 'g'), '');

const updateCookie = (newValue: string): void => {
  if (newValue) {
    Cookies.set(PERSONALIZATION_COOKIE_KEY, newValue, { expires: 1 });
  } else {
    Cookies.remove(PERSONALIZATION_COOKIE_KEY);
  }
};

const addOrUpdatePersonalizationCookie = (key: string, value: string): void => {
  const existing = Cookies.get(PERSONALIZATION_COOKIE_KEY) || '';
  const withoutKey = removeKeyFromCookie(existing, key);
  const existingEntries = withoutKey.split('|').filter(Boolean);
  const allEntries = [...existingEntries, `${key}:${value}`];
  updateCookie(normalize(allEntries.join('|')));
};

const removeFromPersonalizationCookie = (key: string): void => {
  const existing = Cookies.get(PERSONALIZATION_COOKIE_KEY) || '';
  updateCookie(normalize(removeKeyFromCookie(existing, key)));
};

/**
 * `PersonalizationCookieController` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane
 * (`libs/sitecore/components/system/src/PersonalizationCookieController/PersonalizationCookieController.tsx`):
 * renders nothing — it's a pure side effect, adding or removing one key from a shared `xp_pers` cookie
 * that downstream personalization reads. The side effect runs during render, exactly like the legacy
 * component; that's a pre-existing quirk of this specific block (most side-effecting code in this
 * codebase uses `useEffect`), kept rather than "fixed" so behaviour matches on both lanes.
 *
 * Sitecore nests `action`/`key`/`value` under a `cookie` field group; the CMS schema flattened it
 * — see `personalization-cookie-controller-content.types.ts`.
 */
const PersonalizationCookieController: FC = () => {
  const content = useContent<PersonalizationCookieControllerContent>();
  const { action, key, value } = content.cookie ?? content;

  if (action === ACTION_KEYS.addOrUpdate) {
    if (!key || !value) {
      logger.error('QilBkC', 'AddOrUpdate PersonalizationCookie failed', { reason: 'Key or value is missing' });
      return null;
    }
    addOrUpdatePersonalizationCookie(key, value);
  } else if (action === ACTION_KEYS.remove) {
    if (!key) {
      logger.error('MAOknk', 'Remove PersonalizationCookie failed', { reason: 'Key is missing' });
      return null;
    }
    removeFromPersonalizationCookie(key);
  } else {
    logger.error('j4qfbO', 'PersonalizationCookie', { reason: 'Unknown action', action });
  }

  return null;
};

export default PersonalizationCookieController;
